import type { CMSDataRecord } from "./cmsService";
import { CMSService } from "./cmsService";

export interface DataBinding {
  cmsDatasetId: string;
  fieldName: string;
  displayName: string;
  fieldType: string;
}

export interface BoundComponent {
  componentId: string;
  property: string; // e.g., 'content.text', 'content.src', etc.
  binding: DataBinding;
}

export class DataBindingService {
  /**
   * Get available CMS datasets for the current project
   */
  static async getAvailableDatasets(projectId: string): Promise<CMSDataRecord[]> {
    try {
      return await CMSService.loadProjectCMSData(projectId);
    } catch (error) {
      console.error("Error loading CMS datasets:", error);
      return [];
    }
  }

  /**
   * Get fields from a specific CMS dataset
   */
  static async getDatasetFields(datasetId: string, craftspaceId: string): Promise<unknown[]> {
    try {
      const dataset = await CMSService.loadCMSDataWithContent(datasetId, craftspaceId);
      const fields = dataset.fieldStructure || [];

      // Filter out invalid fields to prevent SelectItem errors
      return fields.filter(
        (field: { name?: string } | null | undefined) =>
          field?.name && typeof field.name === "string" && field.name.trim() !== "",
      );
    } catch (error) {
      console.error("Error loading dataset fields:", error);
      return [];
    }
  }

  /**
   * Create a data binding configuration
   */
  static createBinding(
    datasetId: string,
    fieldName: string,
    displayName: string,
    fieldType: string,
  ): DataBinding {
    return {
      cmsDatasetId: datasetId,
      fieldName,
      displayName,
      fieldType,
    };
  }

  /**
   * Apply data binding to component content
   * This resolves the binding and returns the actual data value
   */
  static async resolveBinding(
    binding: DataBinding,
    craftspaceId: string,
    rowIndex: number = 0,
  ): Promise<string> {
    try {
      const dataset = await CMSService.loadCMSDataWithContent(binding.cmsDatasetId, craftspaceId);

      if (!dataset.data || !dataset.data.rows || dataset.data.rows.length === 0) {
        return "";
      }

      const row = dataset.data.rows[rowIndex];
      const value = row?.[binding.fieldName];

      return value !== undefined && value !== null ? String(value) : "";
    } catch (error) {
      console.error("Error resolving data binding:", error);
      return "";
    }
  }

  /**
   * Get suggested property paths for different component types
   */
  static getSuggestedProperties(componentType: string): { path: string; label: string }[] {
    switch (componentType) {
      case "text":
        return [{ path: "content.text", label: "Text Content" }];
      case "image":
        return [
          { path: "content.src", label: "Image URL" },
          { path: "content.alt", label: "Alt Text" },
        ];
      case "video":
        return [
          { path: "content.src", label: "Video URL" },
          { path: "content.title", label: "Video Title" },
        ];
      case "website":
        return [
          { path: "content.src", label: "Website URL" },
          { path: "content.title", label: "Website Title" },
        ];
      default:
        return [];
    }
  }

  /**
   * Check if a component property is bindable
   */
  static isBindableProperty(componentType: string, propertyPath: string): boolean {
    const suggested = DataBindingService.getSuggestedProperties(componentType);
    return suggested.some((prop) => prop.path === propertyPath);
  }
}
